import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["card", "iframe", "mobileButton"]

    connect() {
        this.players = new Map()
        this.observer = null

        this.handleResize = this.debounce(() => {
            this.setupPlayers()
        }, 150)

        // Turbo can connect this controller before the new head script has loaded.
        this.streamScript = document.querySelector('script[src="https://embed.cloudflarestream.com/embed/sdk.latest.js"]')
        this.handleStreamLoad = () => this.setupPlayers()
        this.streamScript?.addEventListener("load", this.handleStreamLoad, { once: true })

        this.setupPlayers()
        window.addEventListener("resize", this.handleResize)
    }

    disconnect() {
        this.teardownObserver()
        window.removeEventListener("resize", this.handleResize)
        this.streamScript?.removeEventListener("load", this.handleStreamLoad)
        this.players.clear()
    }

    setupPlayers() {
        if (typeof window.Stream !== "function") return

        this.teardownObserver()
        this.players.clear()

        const visibleCards = this.cardTargets.filter((card) => this.isVisible(card))

        visibleCards.forEach((card) => {
            const iframe = card.querySelector('[data-autoplay-video-target="iframe"]')
            if (!iframe) return

            const player = window.Stream(iframe)
            this.players.set(iframe, { iframe, player, card })

            // Mobile starts muted and shows the CTA
            if (this.isMobileCard(card)) {
                try {
                    player.muted = true
                } catch (e) {}
                this.showMobileButton()
            } else {
                this.hideMobileButton()
            }
        })

        this.observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const item = this.players.get(entry.target)
                    if (!item) return

                    if (entry.isIntersecting) {
                        item.player.play().catch(() => {})
                    } else {
                        item.player.pause()
                    }
                })
            },
            { threshold: 0.4 }
        )

        this.players.forEach(({ iframe }) => {
            this.observer.observe(iframe)
        })
    }

    async toggleSound(event) {
        event.preventDefault()

        const mobileCard = this.visibleMobileCard()
        if (!mobileCard) return

        const iframe = mobileCard.querySelector('[data-autoplay-video-target="iframe"]')
        const item = this.players.get(iframe)
        if (!item) return

        try {
            const currentlyMuted = !!item.player.muted
            item.player.muted = !currentlyMuted

            if (!currentlyMuted) {
                this.mobileButtonTarget.textContent = "🔊 Tap for sound"
            } else {
                this.mobileButtonTarget.textContent = "🔇 Mute"
            }

            await item.player.play().catch(() => {})
        } catch (e) {
            console.warn("Unable to toggle sound on Cloudflare player", e)
        }
    }

    visibleMobileCard() {
        return this.cardTargets.find((card) => this.isVisible(card) && this.isMobileCard(card))
    }

    isMobileCard(card) {
        return card.classList.contains("d-lg-none")
    }

    isVisible(element) {
        const style = window.getComputedStyle(element)
        return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            element.offsetWidth > 0 &&
            element.offsetHeight > 0
        )
    }

    showMobileButton() {
        if (this.hasMobileButtonTarget) {
            this.mobileButtonTarget.classList.remove("d-none")
        }
    }

    hideMobileButton() {
        if (this.hasMobileButtonTarget) {
            this.mobileButtonTarget.classList.add("d-none")
        }
    }

    teardownObserver() {
        if (this.observer) {
            this.observer.disconnect()
            this.observer = null
        }
    }

    debounce(fn, wait) {
        let timeout

        return (...args) => {
            clearTimeout(timeout)
            timeout = setTimeout(() => fn(...args), wait)
        }
    }
}
